// Optimistic row removal with positional revert.
//
// Review-style lists remove rows the moment the user acts and re-insert them
// at their original positions when the API call fails — mirroring the
// apply-local/revert-on-failure pattern VendorsPage uses for field edits.

export interface RowSnapshot<T> {
  index: number;
  item: T;
}

/** Split `items` into the rows being acted on (as a positional snapshot) and the rest. */
export function takeRows<T extends { id: number }>(
  items: T[],
  ids: Iterable<number>,
): { remaining: T[]; snapshot: RowSnapshot<T>[] } {
  const idSet = new Set(ids);
  const remaining: T[] = [];
  const snapshot: RowSnapshot<T>[] = [];
  items.forEach((item, index) => {
    if (idSet.has(item.id)) {
      snapshot.push({ index, item });
    } else {
      remaining.push(item);
    }
  });
  return { remaining, snapshot };
}

/** Re-insert snapshotted rows at their original positions (clamped to bounds). */
export function restoreRows<T>(items: T[], snapshot: RowSnapshot<T>[]): T[] {
  const result = [...items];
  // Ascending index order keeps later insert positions valid.
  for (const { index, item } of [...snapshot].sort((a, b) => a.index - b.index)) {
    result.splice(Math.min(index, result.length), 0, item);
  }
  return result;
}
